'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

// ─── STATIC INDEX ─────────────────────────────────────────────────────────────
interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  href: string;
  category: 'module' | 'quest' | 'name' | 'glossary' | 'community' | 'letter' | 'observation' | 'phase' | 'resource' | 'chain';
  tags?: string[];
  arabic?: string;
  locality?: string;
  role?: string;
  nameOfAllah?: string;
  ayatRef?: string;
  domain?: string;
  observationCount?: number;
  contributorCount?: number;
}

const MODULES: SearchResult[] = [
  { id: 'open-letter', title: 'Open Letter: Deep Green', subtitle: 'Foundation Document', description: 'The foundation document of the Haal Project — an address to caretakers rooted in Quranic recognition, Sacred Language, and Amanah.', href: '/open-letter', category: 'module', tags: ['letter', 'foundation', 'caretaker', 'amanah'] },
  { id: 'keys', title: 'Keys to Kulli Haal', subtitle: 'Framework Keys', description: 'Five foundational keys: Ayat, Haal, Taqwah, Mizan, Amanah — the operating framework for caretaking consciousness.', href: '/keys-to-kulli-haal', category: 'module', tags: ['keys', 'ayat', 'haal', 'taqwah', 'mizan', 'amanah'] },
  { id: 'irl', title: 'Inner Reality Lens', subtitle: 'Recognition Tool', description: 'An 8-layer recognition methodology for perceiving Ayat in creation — from zahir (apparent) to batin (hidden).', href: '/inner-reality-lens', category: 'module', tags: ['irl', 'lens', 'zahir', 'batin', 'recognition', 'ayat'] },
  { id: 'mrg', title: 'Muslim Resource Guide', subtitle: 'Community Resource', description: 'A city-by-city resource guide for Muslim communities — justice-involved, eco-caretaking, and community connect.', href: '/muslim-resource-guide', category: 'module', tags: ['mrg', 'resource', 'community', 'justice', 'city'] },
  { id: 'ayat', title: 'Ayat in Creation', subtitle: 'Signs Module', description: 'Explore the signs of Allah in creation — from the leaf sequence to cosmic patterns, each a proof of the Creator.', href: '/ayat-in-creation', category: 'module', tags: ['ayat', 'signs', 'creation', 'leaf', 'sequence'] },
  { id: 'names', title: 'Names Reference', subtitle: 'Al-Asma Al-Husna', description: 'The 99 Beautiful Names of Allah — with root meanings, Quranic references, and platform connections.', href: '/names-reference', category: 'module', tags: ['names', 'allah', 'asma', 'husna', 'arabic'] },
  { id: 'glossary', title: 'Sacred Language Glossary', subtitle: 'SL Glossary', description: 'A living glossary of Sacred Language terms used across the platform — Arabic roots, meanings, and platform links.', href: '/sl-glossary', category: 'module', tags: ['glossary', 'arabic', 'sacred', 'language', 'terms'] },
  { id: 'guided-intro', title: 'Guided Introduction', subtitle: 'Onboarding', description: 'A guided introduction to the Haal Project — role selection, framework overview, and first steps.', href: '/guided-intro', category: 'module', tags: ['intro', 'guided', 'onboarding', 'role'] },
  { id: 'guided-walkthrough', title: 'Guided Walkthrough', subtitle: 'Step-by-Step Tour', description: 'A step-by-step walkthrough of all platform modules with interactive explanations.', href: '/guided-walkthrough', category: 'module', tags: ['walkthrough', 'guided', 'tour', 'modules'] },
  { id: 'community', title: 'Community', subtitle: 'Shared Space', description: 'Community feed, leaderboards, shared quests, garment patterns, and email digest subscriptions.', href: '/community', category: 'module', tags: ['community', 'feed', 'leaderboard', 'shared'] },
  { id: 'bridge-irl', title: 'Bridge: IRL Application', subtitle: 'Uboor Bridge', description: 'Apply the Inner Reality Lens to real-world caretaking scenarios — bridge quests and field applications.', href: '/bridge-irl-application', category: 'module', tags: ['bridge', 'irl', 'application', 'uboor'] },
  { id: 'bridge-env', title: 'Bridge: Environment Design', subtitle: 'Uboor Bridge', description: 'Environmental design through the lens of Amanah — how caretakers shape their built environment.', href: '/bridge-environment-design', category: 'module', tags: ['bridge', 'environment', 'design', 'amanah'] },
  { id: 'bridge-rg', title: 'Bridge: Resource Guide', subtitle: 'Uboor Bridge', description: 'Bridging the Muslim Resource Guide to practical caretaking work in your locality.', href: '/bridge-resource-guide', category: 'module', tags: ['bridge', 'resource', 'guide', 'locality'] },
  { id: 'dashboard', title: 'My Journey Dashboard', subtitle: 'Personal Space', description: 'Your customizable caretaker dashboard — widgets, alhamdulilah progress, quests, and personal tools.', href: '/user-dashboard', category: 'module', tags: ['dashboard', 'journey', 'personal', 'widgets', 'alhamdulilah'] },
  { id: 'followers', title: 'Followers & Cohorts', subtitle: 'Community Connections', description: 'Follow caretakers, join role-based cohorts, view activity feeds, and track mutual connections.', href: '/followers', category: 'module', tags: ['followers', 'cohorts', 'connections', 'activity'] },
  { id: 'quests', title: 'Quests', subtitle: 'Caretaker Quests', description: 'Active quests for caretakers — observation tasks, community contributions, and field work.', href: '/quests', category: 'module', tags: ['quests', 'tasks', 'observation', 'field'] },
  { id: 'saved', title: 'Saved Collection', subtitle: 'Bookmarks', description: 'Your saved items — bookmarked sections, names, quests, and community content.', href: '/saved-collection', category: 'module', tags: ['saved', 'bookmarks', 'collection'] },
];

const QUEST_TEMPLATES: SearchResult[] = [
  { id: 'q-observation', title: 'Observation Quest', subtitle: 'IRL Lens Application', description: 'Go outside and apply the IRL Lens to a natural object. Document zahir (apparent) and batin (hidden) layers.', href: '/quests', category: 'quest', tags: ['observation', 'irl', 'nature', 'zahir', 'batin'] },
  { id: 'q-community-map', title: 'Community Mapping Quest', subtitle: 'MRG Application', description: 'Map 3 community resources in your locality using the Muslim Resource Guide framework.', href: '/quests', category: 'quest', tags: ['community', 'mapping', 'mrg', 'locality'] },
  { id: 'q-names-reflection', title: 'Names Reflection Quest', subtitle: 'Names Reference', description: 'Choose one Name of Allah and document how you observed it in your environment this week.', href: '/quests', category: 'quest', tags: ['names', 'reflection', 'observation', 'weekly'] },
  { id: 'q-amanah-audit', title: 'Amanah Audit Quest', subtitle: 'Key 05: Amanah', description: 'Audit one area of your life or work through the lens of Amanah — what are you entrusted with?', href: '/quests', category: 'quest', tags: ['amanah', 'audit', 'trust', 'responsibility'] },
  { id: 'q-garment-share', title: 'Shared Garment Quest', subtitle: 'Community Contribution', description: 'Document and share a garment pattern from your community — a practice, tradition, or craft.', href: '/community', category: 'quest', tags: ['garment', 'share', 'community', 'pattern', 'tradition'] },
  { id: 'q-bridge-field', title: 'Bridge Field Quest', subtitle: 'Uboor Bridge', description: 'Complete a bridge quest connecting your professional role to caretaking principles.', href: '/bridge-irl-application', category: 'quest', tags: ['bridge', 'field', 'professional', 'role'] },
  { id: 'q-obs-chain', title: 'Observation Chain Quest', subtitle: 'Narrative Sequence', description: 'Link 3 or more IRL observations into a narrative sequence and share as a garment.', href: '/inner-reality-lens', category: 'quest', tags: ['observation', 'chain', 'narrative', 'garment', 'sequence'] },
];

const GLOSSARY_TERMS: SearchResult[] = [
  { id: 'g-haal', title: 'Haal', subtitle: 'حال', description: 'State of being — the present condition, circumstance, or situation. "Kulli Haal" means every state.', href: '/sl-glossary', category: 'glossary', arabic: 'حال', tags: ['haal', 'state', 'condition', 'kulli'] },
  { id: 'g-amanah', title: 'Amanah', subtitle: 'أمانة', description: 'Trust, responsibility, stewardship — the sacred trust placed on humanity as caretakers of the earth.', href: '/sl-glossary', category: 'glossary', arabic: 'أمانة', tags: ['amanah', 'trust', 'stewardship', 'caretaker'] },
  { id: 'g-taqwah', title: 'Taqwah', subtitle: 'تقوى', description: 'God-consciousness, mindfulness of Allah — the protective awareness that guides ethical action.', href: '/sl-glossary', category: 'glossary', arabic: 'تقوى', tags: ['taqwah', 'consciousness', 'mindfulness', 'ethics'] },
  { id: 'g-mizan', title: 'Mizan', subtitle: 'ميزان', description: 'Balance, scale, measure — the divine balance in creation that caretakers are called to maintain.', href: '/sl-glossary', category: 'glossary', arabic: 'ميزان', tags: ['mizan', 'balance', 'scale', 'measure'] },
  { id: 'g-ayat', title: 'Ayat', subtitle: 'آيات', description: 'Signs, proofs, evidences — the signs of Allah in creation and in scripture, continually unfolding.', href: '/sl-glossary', category: 'glossary', arabic: 'آيات', tags: ['ayat', 'signs', 'proofs', 'creation'] },
  { id: 'g-mata', title: 'Mata', subtitle: 'متاع', description: 'Provision, tool, temporary benefit — the world as a temporary provision and tool for caretaking.', href: '/sl-glossary', category: 'glossary', arabic: 'متاع', tags: ['mata', 'provision', 'tool', 'temporary'] },
  { id: 'g-uboor', title: 'Uboor', subtitle: 'عبور', description: 'Crossing, bridge, passage — the act of bridging knowledge to practice, theory to field work.', href: '/sl-glossary', category: 'glossary', arabic: 'عبور', tags: ['uboor', 'bridge', 'crossing', 'practice'] },
  { id: 'g-fitra', title: 'Fitra', subtitle: 'فطرة', description: 'Original nature, innate disposition — the natural state of recognition and submission to the Creator.', href: '/sl-glossary', category: 'glossary', arabic: 'فطرة', tags: ['fitra', 'nature', 'innate', 'original'] },
  { id: 'g-islah', title: 'Islah', subtitle: 'إصلاح', description: 'Reform, restoration, reconciliation — to fix, mend, renew. Root of Salah (prayer).', href: '/sl-glossary', category: 'glossary', arabic: 'إصلاح', tags: ['islah', 'reform', 'restore', 'salah'] },
  { id: 'g-zahir', title: 'Zahir', subtitle: 'ظاهر', description: 'Apparent, manifest, outer — the visible, perceptible surface of things. Paired with Batin.', href: '/sl-glossary', category: 'glossary', arabic: 'ظاهر', tags: ['zahir', 'apparent', 'manifest', 'outer'] },
  { id: 'g-batin', title: 'Batin', subtitle: 'باطن', description: 'Hidden, inner, concealed — the inner reality beneath the apparent. Accessible through Basirah.', href: '/sl-glossary', category: 'glossary', arabic: 'باطن', tags: ['batin', 'hidden', 'inner', 'basirah'] },
  { id: 'g-fisabilillah', title: 'Fisabilillah', subtitle: 'في سبيل الله', description: 'In the way of Allah — dedicating action and effort to the path of Allah. The framing for all caretaking work.', href: '/sl-glossary', category: 'glossary', arabic: 'في سبيل الله', tags: ['fisabilillah', 'way', 'allah', 'dedication'] },
];

const LETTER_SECTIONS_INDEX: SearchResult[] = [
  { id: 'ls-recognition', title: 'The Recognition', subtitle: 'Section 1 · Open Letter', description: 'We are made aware of it from within it — the recognition arc: Encounter → Haqq → Amanah.', href: '/open-letter#section-opening', category: 'letter', tags: ['recognition', 'haqq', 'amanah', 'caretaker'] },
  { id: 'ls-qadir', title: 'Al-Qadir', subtitle: 'Section 2 · Open Letter', description: 'The Decree of Allah — the leaf sequence as a complete IRL Lens walkthrough.', href: '/open-letter#section-al-qadir', category: 'letter', tags: ['qadir', 'decree', 'leaf', 'sequence', 'irl'] },
  { id: 'ls-khabir', title: 'Al-Khabir · Al-Zahir', subtitle: 'Section 19 · Open Letter', description: 'Zahir and Batin — the two poles of the IRL Lens, outer encounter and inner recognition.', href: '/open-letter#section-al-khabir-al-zahir', category: 'letter', tags: ['khabir', 'zahir', 'batin', 'irl', 'recognition'] },
  { id: 'ls-steppin', title: 'Steppin · السير', subtitle: 'Section 31 · Open Letter', description: 'As we look to the streets and hear no justice, no peace — a reflection on manifest truth.', href: '/open-letter#section-steppin', category: 'letter', tags: ['justice', 'peace', 'streets', 'community', 'poem'] },
  { id: 'ls-branding', title: 'Branding · الختام', subtitle: 'Section 32 · Open Letter', description: 'Islah = root of Salah. The whole earth has been made a Masjid for the human family.', href: '/open-letter#section-branding-closing', category: 'letter', tags: ['islah', 'salah', 'masjid', 'closing', 'branding'] },
];

// ─── PHASE RESULTS ────────────────────────────────────────────────────────────
const PHASE_RESULTS: SearchResult[] = [
  { id: 'phase-journey', title: 'Journey Phase', subtitle: 'Community Phase', description: 'The first phase of community engagement — entering the path of caretaking with intention.', href: '/phase/journey', category: 'phase', tags: ['journey', 'phase', 'community', 'beginning'] },
  { id: 'phase-tending', title: 'Tending Phase', subtitle: 'Community Phase', description: 'Active caretaking — tending to the community, the earth, and the self with consistent practice.', href: '/phase/tending', category: 'phase', tags: ['tending', 'phase', 'community', 'practice'] },
  { id: 'phase-embodiment', title: 'Embodiment Phase', subtitle: 'Community Phase', description: 'Living the framework — embodying caretaking principles in every domain of life.', href: '/phase/embodiment', category: 'phase', tags: ['embodiment', 'phase', 'community', 'living'] },
];

// ─── RESOURCE RESULTS (city-based) ────────────────────────────────────────────
const RESOURCE_RESULTS: SearchResult[] = [
  { id: 'res-chicago', title: 'Chicago Resources', subtitle: 'Muslim Resource Guide', description: 'Community resources, masajid, eco-caretaking, and justice-involved support in Chicago.', href: '/muslim-resource-guide?city=chicago', category: 'resource', tags: ['chicago', 'community', 'resources', 'masajid'], locality: 'Chicago' },
  { id: 'res-atlanta', title: 'Atlanta Resources', subtitle: 'Muslim Resource Guide', description: 'Community resources, masajid, eco-caretaking, and justice-involved support in Atlanta.', href: '/muslim-resource-guide?city=atlanta', category: 'resource', tags: ['atlanta', 'community', 'resources', 'masajid'], locality: 'Atlanta' },
  { id: 'res-portland', title: 'Portland Resources', subtitle: 'Muslim Resource Guide', description: 'Community resources, masajid, eco-caretaking, and justice-involved support in Portland.', href: '/muslim-resource-guide?city=portland', category: 'resource', tags: ['portland', 'community', 'resources', 'masajid'], locality: 'Portland' },
  { id: 'res-detroit', title: 'Detroit Resources', subtitle: 'Muslim Resource Guide', description: 'Community resources, masajid, eco-caretaking, and justice-involved support in Detroit.', href: '/muslim-resource-guide?city=detroit', category: 'resource', tags: ['detroit', 'community', 'resources', 'masajid'], locality: 'Detroit' },
];

const ALL_STATIC_RESULTS: SearchResult[] = [
  ...MODULES,
  ...QUEST_TEMPLATES,
  ...GLOSSARY_TERMS,
  ...LETTER_SECTIONS_INDEX,
  ...PHASE_RESULTS,
  ...RESOURCE_RESULTS,
];

// ─── OBSERVATION CHAIN INDEX ──────────────────────────────────────────────────
const OBSERVATION_CHAIN_INDEX: SearchResult[] = [
  {
    id: 'chain-leaf', title: 'The Leaf Sequence — Al-Qadir to An-Nur', subtitle: 'Observation Chain · Chicago',
    description: 'A 6-observation chain tracing the Names of Allah through the lifecycle of a single leaf. The foundational chain of the Haal Project.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['leaf', 'sequence', 'ecology', 'names', 'chicago', 'al-qadir', 'an-nur'],
    locality: 'Chicago', nameOfAllah: 'Al-Qadir', domain: 'Ecology', observationCount: 6, contributorCount: 12,
  },
  {
    id: 'chain-mizan', title: 'Mizan in the Streets — Justice Observation Chain', subtitle: 'Observation Chain · Atlanta',
    description: 'A 9-observation chain documenting the presence and absence of Mizan in Atlanta\'s Vine City neighborhood.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['justice', 'mizan', 'urban', 'community', 'al-adl', 'atlanta'],
    locality: 'Atlanta', nameOfAllah: 'Al-Adl', domain: 'Justice', observationCount: 9, contributorCount: 5,
  },
  {
    id: 'chain-mycorrhizal', title: 'Mycorrhizal Network — Underground Amanah', subtitle: 'Observation Chain · Portland',
    description: 'A 7-observation chain exploring mycorrhizal fungal networks as a model of Amanah — the hidden infrastructure of trust.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['ecology', 'mycorrhizal', 'amanah', 'al-latif', 'forest', 'portland'],
    locality: 'Portland', nameOfAllah: 'Al-Latif', domain: 'Ecology', observationCount: 7, contributorCount: 3,
  },
  {
    id: 'chain-40days', title: '40 Days Fisabilillah — Detroit Service Chain', subtitle: 'Observation Chain · Detroit',
    description: 'A 40-observation chain linking consecutive daily acts of service in Detroit. Shared as a garment.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['fisabilillah', 'service', 'detroit', 'daily', 'garment', 'al-wadud'],
    locality: 'Detroit', nameOfAllah: 'Al-Wadud', domain: 'Service', observationCount: 40, contributorCount: 8,
  },
  {
    id: 'chain-reentry', title: 'Reentry as Islah — Observation Chain', subtitle: 'Observation Chain · Atlanta',
    description: 'A 5-observation chain documenting the reentry journey framed through Islah and Al-Tawwab.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['reentry', 'islah', 'justice', 'al-tawwab', 'restoration', 'atlanta'],
    locality: 'Atlanta', nameOfAllah: 'Al-Tawwab', domain: 'Justice & Reentry', observationCount: 5, contributorCount: 4,
  },
  {
    id: 'chain-urban-ayat', title: 'Urban Ayat — Chicago South Side Mapping', subtitle: 'Observation Chain · Chicago',
    description: 'A 12-observation chain mapping Ayat in the urban landscape of Chicago\'s South Side.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['urban', 'ayat', 'chicago', 'signs', 'al-zahir'],
    locality: 'Chicago', nameOfAllah: 'Al-Zahir', domain: 'Ecology', observationCount: 12, contributorCount: 7,
  },
  {
    id: 'chain-water', title: 'Water Cycle as Tawbah — Portland Rain Chain', subtitle: 'Observation Chain · Portland',
    description: 'A 6-observation chain tracing the water cycle as a model of Tawbah — return and renewal.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['water', 'tawbah', 'ecology', 'portland', 'al-ghaffar'],
    locality: 'Portland', nameOfAllah: 'Al-Ghaffar', domain: 'Ecology', observationCount: 6, contributorCount: 2,
  },
  {
    id: 'chain-masjid', title: 'Masjid as Masjid — Detroit Sacred Space Chain', subtitle: 'Observation Chain · Detroit',
    description: 'A 8-observation chain documenting the sacred geography of Detroit\'s east side.',
    href: '/observation-chain-archive', category: 'chain',
    tags: ['masjid', 'sacred', 'detroit', 'community', 'al-quddus'],
    locality: 'Detroit', nameOfAllah: 'Al-Quddus', domain: 'Community', observationCount: 8, contributorCount: 6,
  },
];

const ALL_STATIC_RESULTS_WITH_CHAINS: SearchResult[] = [
  ...MODULES,
  ...QUEST_TEMPLATES,
  ...GLOSSARY_TERMS,
  ...LETTER_SECTIONS_INDEX,
  ...PHASE_RESULTS,
  ...RESOURCE_RESULTS,
  ...OBSERVATION_CHAIN_INDEX,
];

// ─── ROLE CONFIG ──────────────────────────────────────────────────────────────
const ROLE_FILTERS = [
  { id: 'all-roles', label: 'All Roles' },
  { id: 'inner-seeker', label: 'Inner Seeker' },
  { id: 'community-caretaker', label: 'Community' },
  { id: 'academic', label: 'Academic' },
  { id: 'activist', label: 'Activist' },
  { id: 'local-resource', label: 'Local Resource' },
  { id: 'explorer', label: 'Explorer' },
];

const LOCALITY_FILTERS = [
  { id: 'all-localities', label: 'All Cities' },
  { id: 'Chicago', label: 'Chicago' },
  { id: 'Atlanta', label: 'Atlanta' },
  { id: 'Portland', label: 'Portland' },
  { id: 'Detroit', label: 'Detroit' },
];

// Role → relevant categories
const ROLE_CATEGORY_WEIGHTS: Record<string, string[]> = {
  'inner-seeker': ['glossary', 'letter', 'name', 'module'],
  'community-caretaker': ['community', 'resource', 'quest', 'module'],
  'academic': ['letter', 'glossary', 'name', 'module'],
  'activist': ['quest', 'resource', 'community', 'module'],
  'local-resource': ['resource', 'community', 'quest', 'module'],
  'explorer': ['module', 'quest', 'glossary', 'letter'],
};

const CATEGORY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  module: { label: 'Module', color: '#2d7a47', icon: '⬡' },
  quest: { label: 'Quest', color: '#c9a84c', icon: '◈' },
  name: { label: 'Name', color: '#c9a84c', icon: '✦' },
  glossary: { label: 'Glossary', color: '#8a8ab8', icon: '◎' },
  community: { label: 'Community', color: '#2d8a87', icon: '⌘' },
  letter: { label: 'Letter', color: '#D4A82A', icon: '✦' },
  observation: { label: 'Observation', color: '#2d8a87', icon: '◎' },
  phase: { label: 'Phase', color: '#5a5a8a', icon: '◈' },
  resource: { label: 'Resource', color: '#2d7a47', icon: '⬡' },
  chain: { label: 'Obs. Chain', color: '#2d8a87', icon: '⛓' },
};

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'module', label: 'Modules' },
  { id: 'quest', label: 'Quests' },
  { id: 'glossary', label: 'Glossary' },
  { id: 'letter', label: 'Letter' },
  { id: 'phase', label: 'Phases' },
  { id: 'resource', label: 'Resources' },
  { id: 'observation', label: 'Observations' },
  { id: 'chain', label: 'Chains' },
];

// ─── SAVED SEARCHES ───────────────────────────────────────────────────────────
interface SavedSearch {
  id: string;
  query: string;
  filter: string;
  roleFilter: string;
  localityFilter: string;
  savedAt: string;
}

function getSavedSearches(): SavedSearch[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem('haal_saved_searches') || '[]');
  } catch { return []; }
}

function saveSearch(search: Omit<SavedSearch, 'id' | 'savedAt'>) {
  const searches = getSavedSearches();
  const newSearch: SavedSearch = { ...search, id: `ss-${Date.now()}`, savedAt: new Date().toISOString() };
  const updated = [newSearch, ...searches.filter(s => s.query !== search.query)].slice(0, 10);
  localStorage.setItem('haal_saved_searches', JSON.stringify(updated));
  return newSearch;
}

function removeSavedSearch(id: string) {
  const searches = getSavedSearches().filter(s => s.id !== id);
  localStorage.setItem('haal_saved_searches', JSON.stringify(searches));
}

// ─── HIGHLIGHT ────────────────────────────────────────────────────────────────
function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} style={{ background: 'rgba(201,168,76,0.25)', color: '#c9a84c', borderRadius: 2 }}>{part}</mark>
    ) : part
  );
}

function scoreResult(result: SearchResult, query: string, roleFilter: string, localityFilter?: string): number {
  const q = query.toLowerCase();
  let score = 0;
  if (result.title.toLowerCase().includes(q)) score += 10;
  if (result.subtitle?.toLowerCase().includes(q)) score += 6;
  if (result.description.toLowerCase().includes(q)) score += 3;
  if (result.arabic?.toLowerCase().includes(q)) score += 8;
  if (result.tags?.some(t => t.includes(q))) score += 5;
  if (result.nameOfAllah?.toLowerCase().includes(q)) score += 9;
  if (result.domain?.toLowerCase().includes(q)) score += 4;
  // Role boost
  if (roleFilter !== 'all-roles') {
    const preferred = ROLE_CATEGORY_WEIGHTS[roleFilter] || [];
    if (preferred.includes(result.category)) score += 3;
  }
  // Locality-aware boost — surface local results first
  if (localityFilter && localityFilter !== 'all-localities' && result.locality === localityFilter) {
    score += 8; // Strong locality boost
  }
  return score;
}

interface GlobalSearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function GlobalSearchOverlay({ open, onClose }: GlobalSearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all-roles');
  const [localityFilter, setLocalityFilter] = useState('all-localities');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [liveObservations, setLiveObservations] = useState<SearchResult[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [showRoleFilters, setShowRoleFilters] = useState(false);
  const [showLocalityFilters, setShowLocalityFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'constellation'>('list');
  const [mounted, setMounted] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const supabaseRef = useRef(createClient());

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
      setQuery('');
      setFilter('all');
      setSavedSearches(getSavedSearches());
      setShowSaved(false);
    }
  }, [open]);

  // Load live observations from Supabase
  useEffect(() => {
    if (!open) return;
    async function loadObservations() {
      try {
        const { data } = await supabaseRef.current
          .from('irl_observations')
          .select('id, zahir_text, batin_text, name_of_allah, ayat_ref, created_at')
          .order('created_at', { ascending: false })
          .limit(20);
        if (data) {
          setLiveObservations(data.map((obs: any) => ({
            id: `obs-${obs.id}`,
            title: obs.zahir_text?.slice(0, 60) || 'Observation',
            subtitle: obs.name_of_allah ? `Name: ${obs.name_of_allah}` : 'IRL Observation',
            description: obs.batin_text || obs.zahir_text || '',
            href: '/inner-reality-lens',
            category: 'observation' as const,
            tags: ['observation', 'irl', obs.name_of_allah?.toLowerCase(), obs.ayat_ref].filter(Boolean) as string[],
          })));
        }
      } catch { /* silent */ }
    }
    loadObservations();
  }, [open]);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setResults([]);
      return;
    }
    const allResults = [...ALL_STATIC_RESULTS_WITH_CHAINS, ...liveObservations];
    let filtered = allResults
      .filter(r => filter === 'all' || r.category === filter)
      .filter(r => localityFilter === 'all-localities' || !r.locality || r.locality === localityFilter);

    const scored = filtered
      .map(r => ({ result: r, score: scoreResult(r, q, roleFilter, localityFilter) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ result }) => result);
    setResults(scored);
  }, [query, filter, roleFilter, localityFilter, liveObservations]);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      // Arrow key navigation in list view
      if (!open) return;
      const displayResults = query.trim() ? results : popularItems;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(i => Math.min(i + 1, displayResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(i => Math.max(i - 1, -1));
        if (focusedIndex <= 0) inputRef.current?.focus();
      } else if (e.key === 'Enter' && focusedIndex >= 0 && displayResults[focusedIndex]) {
        e.preventDefault();
        window.location.href = displayResults[focusedIndex].href;
        onClose();
      }
    }
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, results, focusedIndex, query]);

  // Reset focused index when results change
  useEffect(() => { setFocusedIndex(-1); }, [results, query]);

  function handleSaveSearch() {
    if (!query.trim()) return;
    const saved = saveSearch({ query: query.trim(), filter, roleFilter, localityFilter });
    setSavedSearches(getSavedSearches());
  }

  function handleLoadSavedSearch(s: SavedSearch) {
    setQuery(s.query);
    setFilter(s.filter);
    setRoleFilter(s.roleFilter);
    setLocalityFilter(s.localityFilter);
    setShowSaved(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleRemoveSavedSearch(id: string) {
    removeSavedSearch(id);
    setSavedSearches(getSavedSearches());
  }

  const popularItems = ALL_STATIC_RESULTS.filter(r =>
    ['open-letter', 'irl', 'keys', 'mrg', 'names', 'q-observation', 'g-amanah', 'g-haal'].includes(r.id)
  );

  const activeFiltersCount = [
    roleFilter !== 'all-roles',
    localityFilter !== 'all-localities',
  ].filter(Boolean).length;

  // Don't render during SSR to prevent hydration mismatch
  if (!mounted) return null;

  return (
    <>
      <style>{`
        .gso-overlay {
          position: fixed; inset: 0;
          background: rgba(6,13,18,0.97);
          backdrop-filter: blur(20px);
          z-index: 400;
          display: flex; flex-direction: column;
          opacity: 0; pointer-events: none;
          transition: opacity 0.25s ease;
        }
        .gso-overlay.open { opacity: 1; pointer-events: all; }
        .gso-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid rgba(201,168,76,0.1);
          flex-shrink: 0;
        }
        .gso-search-row {
          display: flex; align-items: center; gap: 12px;
          max-width: 720px; margin: 0 auto;
        }
        .gso-search-icon { color: rgba(201,168,76,0.5); flex-shrink: 0; }
        .gso-input {
          flex: 1;
          background: transparent;
          border: none; outline: none;
          font-size: 20px;
          color: #f0ece4;
          font-family: 'Georgia', serif;
          caret-color: #c9a84c;
        }
        .gso-input::placeholder { color: rgba(201,168,76,0.3); font-style: italic; }
        .gso-close-btn {
          background: none;
          border: 1px solid rgba(61,54,39,0.4);
          color: #7a6f5d; cursor: pointer;
          padding: 5px 10px; font-size: 10px;
          border-radius: 3px; font-family: inherit;
          transition: all 0.2s; white-space: nowrap;
          letter-spacing: .06em;
        }
        .gso-close-btn:hover { color: #d4c5a9; border-color: rgba(201,168,76,0.3); }
        .gso-filters {
          display: flex; gap: 6px; flex-wrap: wrap;
          max-width: 720px; margin: 12px auto 0;
          align-items: center;
        }
        .gso-filter {
          padding: 3px 12px;
          border: 1px solid rgba(61,54,39,0.3);
          background: transparent; color: #7a6f5d;
          font-size: 9px; cursor: pointer;
          border-radius: 20px; font-family: inherit;
          transition: all 0.2s; letter-spacing: .06em;
          text-transform: uppercase;
        }
        .gso-filter:hover { color: #d4c5a9; }
        .gso-filter.active {
          color: #c9a84c;
          border-color: rgba(201,168,76,0.4);
          background: rgba(201,168,76,0.06);
        }
        .gso-filter.role-active {
          color: #2d8a87;
          border-color: rgba(45,138,135,0.4);
          background: rgba(45,138,135,0.06);
        }
        .gso-filter.locality-active {
          color: #2d7a47;
          border-color: rgba(45,122,71,0.4);
          background: rgba(45,122,71,0.06);
        }
        .gso-filter-group {
          display: flex; gap: 4px; flex-wrap: wrap;
          padding: 6px 10px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 8px;
          max-width: 720px; margin: 8px auto 0;
        }
        .gso-body {
          flex: 1; overflow-y: auto;
          padding: 20px 24px 40px;
        }
        .gso-body::-webkit-scrollbar { width: 4px; }
        .gso-body::-webkit-scrollbar-track { background: transparent; }
        .gso-body::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.2); border-radius: 2px; }
        .gso-section-label {
          font-size: 8px; color: rgba(201,168,76,0.4);
          letter-spacing: .15em; text-transform: uppercase;
          margin-bottom: 12px; max-width: 720px; margin-left: auto; margin-right: auto;
        }
        .gso-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 10px; max-width: 1200px; margin: 0 auto;
        }
        .gso-card {
          background: rgba(10,25,41,0.4);
          border: 1px solid rgba(61,54,39,0.3);
          border-radius: 6px; overflow: hidden;
          transition: border-color 0.2s, background 0.2s;
          display: block;
        }
        .gso-card-main-link {
          text-decoration: none; color: inherit; display: block; outline: none;
        }
        .gso-card:hover {
          border-color: rgba(201,168,76,0.3);
          background: rgba(10,25,41,0.7);
        }
        .gso-card:focus-within, .gso-card.keyboard-focused {
          outline: 2px solid rgba(201,168,76,0.6);
          outline-offset: 2px;
          border-color: rgba(201,168,76,0.4);
          background: rgba(10,25,41,0.7);
        }
        .gso-card-head {
          padding: 8px 12px;
          background: rgba(6,13,18,0.4);
          border-bottom: 1px solid rgba(61,54,39,0.2);
          display: flex; align-items: center; gap: 8px;
        }
        .gso-card-body { padding: 10px 12px; }
        .gso-empty {
          text-align: center; padding: 60px 24px;
          max-width: 400px; margin: 0 auto;
        }
        .gso-kbd {
          display: inline-flex; align-items: center;
          padding: 2px 6px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 3px;
          font-size: 10px; color: rgba(212,197,169,0.5);
          font-family: monospace;
        }
        .gso-saved-item {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 12px; border-radius: 6px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.05);
          cursor: pointer; transition: all 0.15s;
          margin-bottom: 6px;
        }
        .gso-saved-item:hover {
          background: rgba(201,168,76,0.05);
          border-color: rgba(201,168,76,0.2);
        }
        .gso-filter:focus-visible {
          outline: 2px solid rgba(201,168,76,0.5);
          outline-offset: 2px;
        }
        .gso-input:focus-visible { outline: none; }
        @media (max-width: 640px) {
          .gso-header { padding: 16px 16px 12px; }
          .gso-input { font-size: 16px; }
          .gso-body { padding: 16px 16px 40px; }
          .gso-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      <div
        className={`gso-overlay${open ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Global search — press / or ⌘K to open, ESC to close"
        aria-live="polite"
      >
        {/* Header */}
        <div className="gso-header">
          <div className="gso-search-row">
            <span className="gso-search-icon">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="6.5" cy="6.5" r="4.5" />
                <path d="M10.5 10.5l3 3" />
              </svg>
            </span>
            <input
              ref={inputRef}
              className="gso-input"
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search Ayat, Names, observations, phases, resources…"
              autoComplete="off"
              spellCheck={false}
            />
            {/* Save search button */}
            {query.trim() && (
              <button
                className="gso-close-btn"
                onClick={handleSaveSearch}
                title="Save this search"
                style={{ color: '#c9a84c', borderColor: 'rgba(201,168,76,0.3)' }}
              >
                ◈ Save
              </button>
            )}
            {/* Saved searches toggle */}
            <button
              className={`gso-close-btn${savedSearches.length > 0 ? '' : ''}`}
              onClick={() => setShowSaved(v => !v)}
              style={{ position: 'relative' }}
            >
              Saved{savedSearches.length > 0 && <span style={{ marginLeft: 4, color: '#c9a84c' }}>{savedSearches.length}</span>}
            </button>
            <button className="gso-close-btn" onClick={onClose}>ESC</button>
          </div>

          {/* Category filters */}
          <div className="gso-filters">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.id}
                className={`gso-filter${filter === tab.id ? ' active' : ''}`}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            {/* Advanced filter toggles */}
            <button
              className={`gso-filter${showRoleFilters ? ' role-active' : ''}${roleFilter !== 'all-roles' ? ' role-active' : ''}`}
              onClick={() => { setShowRoleFilters(v => !v); setShowLocalityFilters(false); }}
            >
              Role{roleFilter !== 'all-roles' ? ` · ${ROLE_FILTERS.find(r => r.id === roleFilter)?.label}` : ''}
              {activeFiltersCount > 0 && <span style={{ marginLeft: 4, color: '#2d8a87' }}>●</span>}
            </button>
            <button
              className={`gso-filter${showLocalityFilters ? ' locality-active' : ''}${localityFilter !== 'all-localities' ? ' locality-active' : ''}`}
              onClick={() => { setShowLocalityFilters(v => !v); setShowRoleFilters(false); }}
            >
              City{localityFilter !== 'all-localities' ? ` · ${localityFilter}` : ''}
            </button>
          </div>

          {/* Role filter pills */}
          {showRoleFilters && (
            <div className="gso-filter-group">
              <span style={{ fontSize: 8, color: 'rgba(45,138,135,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginRight: 4, alignSelf: 'center' }}>Role:</span>
              {ROLE_FILTERS.map(r => (
                <button
                  key={r.id}
                  className={`gso-filter${roleFilter === r.id ? ' role-active' : ''}`}
                  onClick={() => { setRoleFilter(r.id); setShowRoleFilters(false); }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}

          {/* Locality filter pills */}
          {showLocalityFilters && (
            <div className="gso-filter-group">
              <span style={{ fontSize: 8, color: 'rgba(45,122,71,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginRight: 4, alignSelf: 'center' }}>City:</span>
              {LOCALITY_FILTERS.map(l => (
                <button
                  key={l.id}
                  className={`gso-filter${localityFilter === l.id ? ' locality-active' : ''}`}
                  onClick={() => { setLocalityFilter(l.id); setShowLocalityFilters(false); }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="gso-body">
          {/* Saved searches panel */}
          {showSaved && (
            <div style={{ maxWidth: 720, margin: '0 auto 24px' }}>
              <div className="gso-section-label" style={{ marginBottom: 10 }}>
                SAVED SEARCHES — {savedSearches.length} bookmarked
              </div>
              {savedSearches.length === 0 ? (
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 11, fontStyle: 'italic' }}>No saved searches yet. Search for something and click ◈ Save.</p>
              ) : (
                savedSearches.map(s => (
                  <div key={s.id} className="gso-saved-item" onClick={() => handleLoadSavedSearch(s)}>
                    <span style={{ color: '#c9a84c', fontSize: 12 }}>◈</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, color: '#d4c5a9' }}>{s.query}</span>
                      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                        {s.filter !== 'all' && <span style={{ fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>{s.filter}</span>}
                        {s.roleFilter !== 'all-roles' && <span style={{ fontSize: 8, color: 'rgba(45,138,135,0.5)' }}>{s.roleFilter}</span>}
                        {s.localityFilter !== 'all-localities' && <span style={{ fontSize: 8, color: 'rgba(45,122,71,0.5)' }}>{s.localityFilter}</span>}
                      </div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); handleRemoveSavedSearch(s.id); }}
                      style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.2)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                    >×</button>
                  </div>
                ))
              )}
            </div>
          )}

          {query.trim() === '' ? (
            <>
              <div className="gso-section-label" style={{ marginBottom: 12 }}>POPULAR DESTINATIONS</div>
              <div className="gso-grid">
                {popularItems.map(item => (
                  <SearchCard key={item.id} result={item} query="" onClose={onClose} />
                ))}
              </div>
              {/* Observation Chain Archive shortcut */}
              <div style={{ maxWidth: 720, margin: '24px auto 0', padding: '14px 18px', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.12em', textTransform: 'uppercase', color: '#2d8a87', marginBottom: 3 }}>⛓ OBSERVATION CHAIN ARCHIVE</p>
                  <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)' }}>Browse all shared Observation Chains — by city, domain, or Name of Allah</p>
                </div>
                <Link href="/observation-chain-archive" onClick={onClose} style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', padding: '6px 14px', borderRadius: 4, border: '1px solid rgba(45,138,135,0.35)', color: '#2d8a87', textDecoration: 'none', flexShrink: 0 }}>
                  Browse →
                </Link>
              </div>
              <div style={{ textAlign: 'center', marginTop: 32, color: 'rgba(212,197,169,0.2)', fontSize: 11 }}>
                <span className="gso-kbd">↑↓</span> navigate &nbsp;
                <span className="gso-kbd">↵</span> open &nbsp;
                <span className="gso-kbd">ESC</span> close
              </div>
            </>
          ) : results.length === 0 ? (
            <div className="gso-empty">
              <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.2 }}>◎</div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 13, fontStyle: 'italic', marginBottom: 8 }}>
                No results for &ldquo;{query}&rdquo;
              </p>
              <p style={{ color: 'rgba(212,197,169,0.2)', fontSize: 11 }}>
                Try searching for a module name, Arabic term, or concept
              </p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1200, margin: '0 auto 16px' }}>
                <span className="gso-section-label" style={{ margin: 0 }}>
                  {results.length} result{results.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
                  {localityFilter !== 'all-localities' && <span style={{ color: '#2d7a47', marginLeft: 8 }}>· 📍 {localityFilter} first</span>}
                  {roleFilter !== 'all-roles' && <span style={{ color: '#2d8a87', marginLeft: 8 }}>· {ROLE_FILTERS.find(r => r.id === roleFilter)?.label}</span>}
                </span>
                {/* View mode toggle */}
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => setViewMode('list')}
                    style={{
                      fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                      background: viewMode === 'list' ? 'rgba(201,168,76,0.1)' : 'transparent',
                      border: `1px solid ${viewMode === 'list' ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      color: viewMode === 'list' ? '#c9a84c' : 'rgba(212,197,169,0.3)',
                    }}
                  >
                    ⊞ List
                  </button>
                  <button
                    onClick={() => setViewMode('constellation')}
                    style={{
                      fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                      background: viewMode === 'constellation' ? 'rgba(138,138,184,0.1)' : 'transparent',
                      border: `1px solid ${viewMode === 'constellation' ? 'rgba(138,138,184,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      color: viewMode === 'constellation' ? '#8a8ab8' : 'rgba(212,197,169,0.3)',
                    }}
                  >
                    ✦ Ayat Constellation
                  </button>
                </div>
              </div>
              {viewMode === 'constellation' ? (
                <AyatConstellationView results={results} query={query} onClose={onClose} />
              ) : (
                <div className="gso-grid" role="list" aria-label="Search results">
                  {results.map((item, idx) => (
                    <SearchCard key={item.id} result={item} query={query} onClose={onClose} isFocused={focusedIndex === idx} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── AYAT CONSTELLATION VIEW ──────────────────────────────────────────────────

function AyatConstellationView({ results, query, onClose }: { results: SearchResult[]; query: string; onClose: () => void }) {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [focusedNodeIndex, setFocusedNodeIndex] = useState(-1);
  const svgRef = useRef<SVGSVGElement>(null);

  // Build constellation nodes from results
  const nodes = results.slice(0, 18).map((r, i) => {
    const cat = CATEGORY_CONFIG[r.category] || CATEGORY_CONFIG.module;
    const angle = (i / Math.min(results.length, 18)) * 2 * Math.PI;
    const ring = i < 6 ? 1 : i < 12 ? 2 : 3;
    const radius = ring === 1 ? 120 : ring === 2 ? 200 : 270;
    const cx = 340 + Math.cos(angle) * radius;
    const cy = 280 + Math.sin(angle) * radius;
    return { ...r, cx, cy, cat, ring, angle };
  });

  // Build connections between related nodes (same category or shared tags)
  const connections: { from: number; to: number; strength: number }[] = [];
  nodes.forEach((a, i) => {
    nodes.forEach((b, j) => {
      if (i >= j) return;
      const sharedTags = (a.tags || []).filter(t => (b.tags || []).includes(t)).length;
      const sameCategory = a.category === b.category;
      const sameLocality = a.locality && b.locality && a.locality === b.locality;
      const strength = sharedTags * 2 + (sameCategory ? 1 : 0) + (sameLocality ? 2 : 0);
      if (strength > 0) connections.push({ from: i, to: j, strength });
    });
  });

  const activeResult = activeNode ? results.find(r => r.id === activeNode) : null;

  // Arrow key navigation within constellation
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedNodeIndex(i => {
          const next = (i + 1) % nodes.length;
          setActiveNode(nodes[next]?.id || null);
          return next;
        });
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedNodeIndex(i => {
          const prev = i <= 0 ? nodes.length - 1 : i - 1;
          setActiveNode(nodes[prev]?.id || null);
          return prev;
        });
      } else if (e.key === 'Enter' && focusedNodeIndex >= 0 && nodes[focusedNodeIndex]) {
        e.preventDefault();
        window.location.href = nodes[focusedNodeIndex].href;
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [nodes, focusedNodeIndex, onClose]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <p style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.6)' }}>
          ✦ AYAT CONSTELLATION — {nodes.length} signs connected
        </p>
        <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic' }}>
          Hover a node to reveal connections · Click to navigate
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
        {/* SVG Constellation */}
        <div style={{
          background: 'rgba(6,13,18,0.9)',
          border: '1px solid rgba(138,138,184,0.15)',
          borderRadius: 12, overflow: 'hidden',
          position: 'relative',
        }}>
          <svg
            ref={svgRef}
            viewBox="0 0 680 560"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          >
            {/* Background stars */}
            {Array.from({ length: 40 }).map((_, i) => (
              <circle
                key={`star-${i}`}
                cx={(i * 137.5) % 680}
                cy={(i * 97.3) % 560}
                r={0.5 + (i % 3) * 0.4}
                fill="rgba(212,197,169,0.15)"
              />
            ))}

            {/* Connection lines */}
            {connections.map((conn, i) => {
              const a = nodes[conn.from];
              const b = nodes[conn.to];
              if (!a || !b) return null;
              const isActive = activeNode === a.id || activeNode === b.id;
              return (
                <line
                  key={`conn-${i}`}
                  x1={a.cx} y1={a.cy}
                  x2={b.cx} y2={b.cy}
                  stroke={isActive ? 'rgba(201,168,76,0.4)' : 'rgba(138,138,184,0.08)'}
                  strokeWidth={isActive ? conn.strength * 0.6 : 0.5}
                  strokeDasharray={conn.strength < 2 ? '3 4' : undefined}
                  style={{ transition: 'all 0.3s' }}
                />
              );
            })}

            {/* Center label */}
            <text x="340" y="275" textAnchor="middle" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(201,168,76,0.3)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
              {query.toUpperCase()}
            </text>
            <circle cx="340" cy="280" r="4" fill="rgba(201,168,76,0.3)" />

            {/* Nodes */}
            {nodes.map((node) => {
              const isActive = activeNode === node.id;
              return (
                <g
                  key={node.id}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setActiveNode(node.id)}
                  onMouseLeave={() => setActiveNode(null)}
                  onClick={() => { window.location.href = node.href; onClose(); }}
                >
                  {/* Glow ring */}
                  {isActive && (
                    <circle
                      cx={node.cx} cy={node.cy}
                      r={22}
                      fill="none"
                      stroke={node.cat.color}
                      strokeWidth="1"
                      opacity="0.4"
                    />
                  )}
                  {/* Node circle */}
                  <circle
                    cx={node.cx} cy={node.cy}
                    r={isActive ? 14 : 10}
                    fill={isActive ? `${node.cat.color}30` : 'rgba(6,13,18,0.8)'}
                    stroke={node.cat.color}
                    strokeWidth={isActive ? 1.5 : 0.8}
                    style={{ transition: 'all 0.2s' }}
                  />
                  {/* Icon */}
                  <text
                    x={node.cx} y={node.cy + 4}
                    textAnchor="middle"
                    style={{ fontSize: isActive ? 11 : 9, fill: node.cat.color, fontFamily: 'monospace' }}
                  >
                    {node.cat.icon}
                  </text>
                  {/* Label */}
                  <text
                    x={node.cx}
                    y={node.cy + (isActive ? 28 : 24)}
                    textAnchor="middle"
                    style={{
                      fontSize: 7, fill: isActive ? '#f0ece4' : 'rgba(212,197,169,0.5)',
                      fontFamily: 'monospace', letterSpacing: '.04em',
                    }}
                  >
                    {node.title.length > 16 ? node.title.slice(0, 14) + '…' : node.title}
                  </text>
                  {/* Locality dot */}
                  {node.locality && (
                    <circle
                      cx={node.cx + 10} cy={node.cy - 10}
                      r={3}
                      fill="#2d7a47"
                      opacity={0.8}
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {Object.entries(CATEGORY_CONFIG).slice(0, 6).map(([key, cfg]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, color: cfg.color }}>{cfg.icon}</span>
                <span style={{ fontSize: 7, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)' }}>{cfg.label}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <circle style={{ width: 6, height: 6, borderRadius: '50%', background: '#2d7a47', display: 'inline-block' }} />
              <span style={{ fontSize: 7, fontFamily: 'monospace', letterSpacing: '.06em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)' }}>Local</span>
            </div>
          </div>
        </div>

        {/* Detail panel */}
        <div style={{
          background: 'rgba(6,13,18,0.7)',
          border: '1px solid rgba(138,138,184,0.15)',
          borderRadius: 8, padding: 16,
          minHeight: 200,
        }}>
          {activeResult ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: (CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).color }}>
                    {(CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).icon}
                  </span>
                  <span style={{ fontSize: 8, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: (CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).color }}>
                    {(CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).label}
                  </span>
                  {activeResult.locality && (
                    <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#2d7a47', marginLeft: 'auto' }}>📍 {activeResult.locality}</span>
                  )}
                </div>
                <h3 style={{ color: '#F5F0E8', fontSize: 13, fontWeight: 600, lineHeight: 1.4, marginBottom: 6 }}>
                  {activeResult.title}
                </h3>
                {activeResult.subtitle && (
                  <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace', marginBottom: 8 }}>{activeResult.subtitle}</p>
                )}
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, marginBottom: 10 }}>
                  {activeResult.description}
                </p>
                {activeResult.nameOfAllah && (
                  <p style={{ fontSize: 9, fontFamily: 'monospace', color: '#c9a84c', marginBottom: 6 }}>✦ {activeResult.nameOfAllah}</p>
                )}
                {activeResult.observationCount && (
                  <p style={{ fontSize: 9, fontFamily: 'monospace', color: '#2d8a87', marginBottom: 6 }}>
                    ◎ {activeResult.observationCount} obs
                  </p>
                )}
                {activeResult.contributorCount && (
                  <p style={{ fontSize: 9, fontFamily: 'monospace', color: '#2d7a47', marginBottom: 6 }}>
                    ⬡ {activeResult.contributorCount} contributors
                  </p>
                )}
                {activeResult.tags && activeResult.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    {activeResult.tags.slice(0, 4).map(tag => (
                      <span key={tag} style={{
                        fontSize: 7, color: 'rgba(212,197,169,0.3)',
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 2, padding: '1px 5px',
                        letterSpacing: '.04em',
                      }}>{tag}</span>
                    ))}
                  </div>
                )}

                {/* Cross-reference count + observation/contributor counts */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  {crossRefCount > 0 && (
                    <span style={{
                      fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em',
                      color: 'rgba(138,138,184,0.6)',
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                    }}>
                      ⛓ {crossRefCount} cross-ref{crossRefCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  {activeResult.observationCount !== undefined && (
                    <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#2d8a87' }}>
                      ◎ {activeResult.observationCount} obs
                    </span>
                  )}
                  {activeResult.contributorCount !== undefined && (
                    <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#2d7a47' }}>
                      ⬡ {activeResult.contributorCount} contributors
                    </span>
                  )}
                  {activeResult.nameOfAllah && (
                    <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#c9a84c' }}>
                      ✦ {activeResult.nameOfAllah}
                    </span>
                  )}
                </div>

                {/* Related-to suggestions */}
                {relatedResults.length > 0 && (
                  <div style={{
                    marginTop: 8, paddingTop: 8,
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    <p style={{ fontSize: 7, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.25)', marginBottom: 5 }}>
                      Related to
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {relatedResults.map(rel => {
                        const relCat = CATEGORY_CONFIG[rel.category] || CATEGORY_CONFIG.module;
                        return (
                          <Link
                            key={rel.id}
                            href={rel.href}
                            onClick={onClose}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              fontSize: 9, color: 'rgba(212,197,169,0.45)',
                              textDecoration: 'none',
                              padding: '3px 6px', borderRadius: 4,
                              background: 'rgba(255,255,255,0.02)',
                              border: '1px solid rgba(255,255,255,0.04)',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#d4c5a9'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(212,197,169,0.45)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.04)'; }}
                          >
                            <span style={{ color: relCat.color, fontSize: 8 }}>{relCat.icon}</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rel.title}</span>
                            <span style={{ fontSize: 7, color: relCat.color, opacity: 0.6, flexShrink: 0 }}>{relCat.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <Link
                href={activeResult.href}
                onClick={onClose}
                style={{
                  display: 'block', textAlign: 'center',
                  fontSize: 9, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '8px 14px', borderRadius: 4,
                  background: `${(CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).color}15`,
                  border: `1px solid ${(CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).color}40`,
                  color: (CATEGORY_CONFIG[activeResult.category] || CATEGORY_CONFIG.module).color,
                  textDecoration: 'none',
                }}
              >
                Open →
              </Link>
            </>
          ) : (
            <div style={{ textAlign: 'center', paddingTop: 40 }}>
              <div style={{ fontSize: 24, marginBottom: 12, opacity: 0.15 }}>✦</div>
              <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 11, fontStyle: 'italic' }}>
                Hover a node to see details
              </p>
              <p style={{ color: 'rgba(212,197,169,0.2)', fontSize: 10, marginTop: 6 }}>
                Lines show connections between signs
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SearchCard({ result, query, onClose, isFocused = false }: { result: SearchResult; query: string; onClose: () => void; isFocused?: boolean }) {
  const cat = CATEGORY_CONFIG[result.category] || CATEGORY_CONFIG.module;
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Scroll into view when keyboard-focused
  React.useEffect(() => {
    if (isFocused && cardRef.current) {
      cardRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isFocused]);

  // Cross-reference count: how many other results share tags with this result
  const crossRefCount = ALL_STATIC_RESULTS_WITH_CHAINS.filter(r =>
    r.id !== result.id &&
    (r.tags || []).some(t => (result.tags || []).includes(t))
  ).length;

  // Related-to suggestions: top 2 results sharing the most tags
  const relatedResults = ALL_STATIC_RESULTS_WITH_CHAINS
    .filter(r => r.id !== result.id)
    .map(r => ({
      r,
      shared: (r.tags || []).filter(t => (result.tags || []).includes(t)).length,
    }))
    .filter(({ shared }) => shared > 0)
    .sort((a, b) => b.shared - a.shared)
    .slice(0, 2)
    .map(({ r }) => r);

  return (
    <div
      ref={cardRef}
      className={`gso-card${isFocused ? ' keyboard-focused' : ''}`}
      role="listitem"
    >
      <Link
        href={result.href}
        className="gso-card-main-link"
        onClick={onClose}
        aria-label={`${result.title} — ${cat.label}${result.locality ? ` in ${result.locality}` : ''}`}
        tabIndex={0}
      >
        <div className="gso-card-head">
          <span style={{
            fontSize: 7, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '2px 7px', borderRadius: 20,
            background: `${cat.color}15`, border: `1px solid ${cat.color}40`, color: cat.color,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            {cat.icon} {cat.label}
          </span>
          {result.subtitle && (
            <>
              <span style={{ color: 'rgba(212,197,169,0.2)', fontSize: 10 }}>·</span>
              <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)' }}>{result.subtitle}</span>
            </>
          )}
          {result.locality && (
            <span style={{ marginLeft: 'auto', fontSize: 8, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace' }}>
              📍 {result.locality}
            </span>
          )}
          {result.arabic && !result.locality && (
            <span style={{ marginLeft: 'auto', fontFamily: "'Amiri', serif", fontSize: 14, color: 'rgba(201,168,76,0.5)' }}>
              {result.arabic}
            </span>
          )}
        </div>
        <div className="gso-card-body">
          <div style={{ fontSize: 13, color: '#f0ece4', marginBottom: 4, fontWeight: 500 }}>
            {highlight(result.title, query)}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.5, marginBottom: 8 }}>
            {highlight(result.description, query)}
          </div>

          {/* Cross-reference count + observation/contributor counts */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            {crossRefCount > 0 && (
              <span style={{
                fontSize: 8, fontFamily: 'monospace', letterSpacing: '.06em',
                color: 'rgba(138,138,184,0.6)',
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}>
                ⛓ {crossRefCount} cross-ref{crossRefCount !== 1 ? 's' : ''}
              </span>
            )}
            {result.observationCount !== undefined && (
              <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#2d8a87' }}>
                ◎ {result.observationCount} obs
              </span>
            )}
            {result.contributorCount !== undefined && (
              <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#2d7a47' }}>
                ⬡ {result.contributorCount} contributors
              </span>
            )}
            {result.nameOfAllah && (
              <span style={{ fontSize: 8, fontFamily: 'monospace', color: '#c9a84c' }}>
                ✦ {result.nameOfAllah}
              </span>
            )}
          </div>

          {result.tags && result.tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: relatedResults.length > 0 ? 10 : 0 }}>
              {result.tags.slice(0, 4).map(tag => (
                <span key={tag} style={{
                  fontSize: 8, color: 'rgba(212,197,169,0.3)',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 2, padding: '1px 5px',
                  letterSpacing: '.04em',
                }}>{tag}</span>
              ))}
            </div>
          )}
        </div>
      </Link>

      {/* Related links must not be inside the main card <a> (invalid HTML + hydration error). */}
      {relatedResults.length > 0 && (
        <div className="gso-card-body" style={{ paddingTop: 0 }}>
          <div style={{
            marginTop: 0, paddingTop: 8,
            borderTop: '1px solid rgba(255,255,255,0.05)',
          }}>
            <p style={{ fontSize: 7, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.25)', marginBottom: 5 }}>
              Related to
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {relatedResults.map(rel => {
                const relCat = CATEGORY_CONFIG[rel.category] || CATEGORY_CONFIG.module;
                return (
                  <Link
                    key={rel.id}
                    href={rel.href}
                    onClick={onClose}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      fontSize: 9, color: 'rgba(212,197,169,0.45)',
                      textDecoration: 'none',
                      padding: '3px 6px', borderRadius: 4,
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.04)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#d4c5a9'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(212,197,169,0.45)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.04)'; }}
                  >
                    <span style={{ color: relCat.color, fontSize: 8 }}>{relCat.icon}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rel.title}</span>
                    <span style={{ fontSize: 7, color: relCat.color, opacity: 0.6, flexShrink: 0 }}>{relCat.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
